/* @flow */
import React, { useState, useRef, useEffect, useContext } from "react";
import styled, { css } from "styled-components";
import { withRouter } from "react-router-dom";
import idx from "idx";

import { NEWS_LINK } from "../common/fetch";
import { OptionalLink as Link } from "../common/Link";
import Gettext from "../common/Gettext";
import Scrollable from "../common/Scrollable";
import { withDeposit } from "../common/withDeposit";
import { fontSize } from "../common/styled-mixins";
import withData from "../withData";
import FeatureFlags from "../../FeatureFlags";
import { isIronTerminalIosApp, iOSAppPostMessage } from "../utils";

import Language from "./Language";

import {
  TradingIcon,
  TradingHistoryIcon,
  DepositeIcon,
  WithdrawIcon,
  WithdrawHistoryIcon,
  SupportIcon,
  TournamentsIcon,
  NewsIcon,
  QuitIcon,
  HelpCenterIcon,
} from "./Icons";
import { gaReducer } from "../GA";
import {
  useRelevantTournamentInvitationsCount,
  useSuggestedPromoCode,
} from "../Bell/Labels";
import TournamentApi from "../Tournaments/Api";
import Bell from "../Bell";
import { allNews } from "../Bell/helpers";
import { getCompleteLocale } from "../common/Link";

const isEztrader = process.env.REACT_APP_DEPLOY_TARGET === "eztrader";

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  padding: 14px 21px;
  background-color: ${({ theme }) => theme.navigationBg};
  color: ${({ theme }) => theme.navigationItemColor};
  overflow: hidden;

  ${({ theme }) => {
    if (theme.direction === "rtl") {
      return css`
        padding-right: 28px;
        padding-left: 11px;
      `;
    }

    return css`
      padding-left: 28px;
      padding-right: 11px;
    `;
  }};
`;

const ScrollableInner = styled.div`
  position: relative;
  padding-right: ${({ theme }) => (theme.direction === "rtl" ? 0 : "15px")};
  padding-left: ${({ theme }) => (theme.direction === "rtl" ? "15px" : 0)};
`;

const Submenu = styled.div`
  padding: 7px 0 10px;
  height: auto;
`;

export const ItemView = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  margin-bottom: 3px;
  color: inherit;

  ${(props) =>
    props.active &&
    css`
      color: ${({ theme }) => theme.navigationItemColor};
      background-color: ${(props) => props.theme.navigationItemActiveBg};
    `};
`;

const StyledItemView = styled(ItemView)`
  padding: 9px;
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "14px"};
  line-height: 19px;
  color: ${({ theme }) => theme.navigationItemColor};
  text-decoration: none;

  ${({ theme }) => {
    if (theme.direction === "rtl") {
      return css`
        padding-right: 14px;
        padding-left: 11px;
      `;
    }

    return css`
      padding-left: 14px;
      padding-right: 11px;
    `;
  }};

  :hover,
  :focus {
    color: ${(props) => props.theme.accent};
    text-decoration: none;
    outline: none;
  }
`;

export const IconView = styled.i`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 19px;
  height: 19px;
  color: ${({ theme }) => theme.navigationItemIcon};
  backface-visibility: hidden;

  ${({ theme }) => {
    if (theme.direction === "rtl") {
      return css`
        margin-left: 10px;
      `;
    }

    return css`
      margin-right: 10px;
    `;
  }};

  ${(props) =>
    props.active &&
    css`
      color: ${({ theme }) => theme.navigationItemIconActive};
    `};
`;

const StyledLink = styled(Link)`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  width: 100%;
  padding: 6px;
  font-size: ${({ theme }) =>
    theme.locale === "ar" ? theme.minFontSize : "14px"};
  line-height: 1;
  text-align: left;
  text-decoration: none;
  color: ${({ theme }) => theme.navigationItemColor};
  cursor: pointer;

  ${({ theme }) => {
    if (theme.direction === "rtl") {
      return css`
        padding-right: 41px;
        padding-left: 15px;
      `;
    }

    return css`
      padding-left: 41px;
      padding-right: 15px;
    `;
  }};

  :hover,
  :focus {
    color: ${(props) => props.theme.accent};
    text-decoration: none;
  }
`;

const StyledItemLink = styled(StyledLink)`
  padding: 9px;
  line-height: 19px;

  ${({ theme }) => {
    if (theme.direction === "rtl") {
      return css`
        padding-right: 14px;
        padding-left: 11px;
      `;
    }

    return css`
      padding-left: 14px;
      padding-right: 11px;
    `;
  }};
`;

const SupportItem = styled(StyledItemView)`
  position: relative;
  cursor: pointer;
  user-select: none;

  :after {
    content: "";
    position: absolute;
    top: 50%;
    transform: translateY(-50%) rotate(0deg);
    transform-origin: 50%;
    margin-top: 1px;
    border: 4px solid transparent;
    border-top: 6px solid ${({ theme }) => theme.staticHeaderLink};
    border-bottom: none;
    transition: transform 0.3s ease;
    backface-visibility: hidden;

    ${({ theme }) => {
      if (theme.direction === "rtl") {
        return css`
          left: 11px;
        `;
      }

      return css`
        right: 11px;
      `;
    }};
  }

  ${(props) =>
    props.active &&
    css`
      :after {
        transform: translateY(-50%) rotate(-180deg);
      }
    `};
`;

const Indicator = styled.div`
  flex: 0 0 auto;
  background-color: #0085eb;
  min-width: 20px;
  padding: 0 5px;
  border-radius: 5px 5px 5px 0;
  color: #fff;
  text-align: center;
  font-size: ${fontSize(12)};
  font-weight: 400;
`;

const MenuTitle = styled.div`
  flex-grow: 1;
`;

type SharedProps = {
  onClick?: () => void,
  pathname: string,
  href: string,
  title: React.Element<React.ComponentType<{ children: any }>>,
  indicator?: React.Node,
};

type ItemProps = {
  Icon: () => React.Element<any>,
};

function Item({
  pathname,
  href,
  externalHref,
  title,
  Icon,
  indicator,
  onClick,
  bellActive,
  ...rest
}: SharedProps & ItemProps) {
  const isActive = bellActive ? false : pathname === href;
  const isNews = href === "/news";
  const api = useContext(TournamentApi);

  const news =
    isNews && idx(api.readEntity("/helpdesk/api/news"), (n) => n.value.data);
  const newsTimestamp =
    isNews && idx(api.readEntity("/api/news"), (n) => n.value);

  if (isNews && (news == null || newsTimestamp == null)) {
    return null;
  }

  let new_news_count = 0;

  if (isNews) {
    new_news_count = allNews({
      news,
      lastViewTimestamp: newsTimestamp.lastViewTimestamp,
    });
  }

  return (
    <ItemView
      active={isActive}
      onClick={(e) => {
        gaReducer("CLICKLINK_NAVIGATION", {
          linkUrl: href,
          linkText: e.currentTarget.textContent,
        });

        return isActive ? onClick() : null;
      }}
    >
      <StyledItemLink
        to={externalHref ? null : href}
        href={externalHref}
        {...rest}
      >
        <IconView active={isActive}>
          <Icon />
        </IconView>
        <MenuTitle>{title}</MenuTitle>
        {indicator && <Indicator>{indicator}</Indicator>}
        {isNews && new_news_count > 0 && (
          <Indicator>{new_news_count}</Indicator>
        )}
      </StyledItemLink>
    </ItemView>
  );
}

function ItemTournaments(props) {
  let api = useContext(TournamentApi);

  useEffect(() => {
    api.fetchEntity("/api/bell-tolls-items");
  }, []);

  let invitations = useRelevantTournamentInvitationsCount();

  if (invitations == null || invitations === 0) {
    return <Item {...props} />;
  } else {
    return <Item {...props} indicator={invitations} />;
  }
}

function ItemFillup(props) {
  let promocode = useSuggestedPromoCode();

  if (promocode == null) {
    return <Item {...props} href="/fillup" />;
  } else {
    return (
      <Item {...props} href={"/fillup?promocode=" + promocode} indicator="%" />
    );
  }
}

function SimpleLink(props: SharedProps) {
  const isActive = props.pathname === props.href;

  return isActive ? (
    <StyledLink as="div" onClick={props.onClick}>
      {props.title}
    </StyledLink>
  ) : (
    <StyledLink to={props.href}>{props.title}</StyledLink>
  );
}

type Props = {
  location: Object,
  locale: string,
  apiConfigs: Object,
  isLoggedIn: boolean,
  isRealAcc: boolean,
  showDeposit: boolean,
  onClick: () => void,
  changeLocaleFetching: () => void,
  onLocaleChange: (value: string) => void,
};

function Menu(props: Props) {
  const {
    location,
    locale,
    onClick,
    isLoggedIn,
    showDeposit,
    isRealAcc,
  } = props;
  const { pathname } = location;

  const items = [
    {
      title: <Gettext>Trading</Gettext>,
      Icon: TradingIcon,
      href: "/trade",
    },
    {
      component: ItemTournaments,
      title: <Gettext>Tournaments</Gettext>,
      Icon: TournamentsIcon,
      href: "/tournaments",
      show: !isEztrader,
    },
    {
      component: Bell,
    },
    {
      title: <Gettext>Trading history</Gettext>,
      Icon: TradingHistoryIcon,
      href: "/trading-history",
    },
    {
      component: ItemFillup,
      title: <Gettext>Deposit</Gettext>,
      Icon: DepositeIcon,
      href: "/fillup",
      showDeposit,
    },
    {
      title: <Gettext>Withdrawal</Gettext>,
      Icon: WithdrawIcon,
      href: "/withdraw",
      showDeposit,
    },
    {
      title: <Gettext>Financial history</Gettext>,
      Icon: WithdrawHistoryIcon,
      href: "/finance-history",
      showDeposit,
    },
    {
      title: <Gettext>News</Gettext>,
      Icon: NewsIcon,
      href: NEWS_LINK ? NEWS_LINK : "/news",
      externalHref: NEWS_LINK + `?lang=${getCompleteLocale(locale)}`,
      show: !isEztrader,
      showDeposit,
    },
    {
      title: <Gettext>Help Center</Gettext>,
      Icon: HelpCenterIcon,
      href: "/helpdesk",
      showDeposit,
    },
  ];

  const simpleItems = [
    {
      title: <Gettext>Analytics</Gettext>,
      href: "/analytics",
      show: isRealAcc,
    },
    {
      title: <Gettext>Video Tutorial</Gettext>,
      href: "/helpdesk/video",
    },
    {
      title: <Gettext>FAQ</Gettext>,
      href: "/helpdesk/faq",
    },
    {
      title: <Gettext>About us</Gettext>,
      href: "/helpdesk/safety#about",
    },
  ];

  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const submenuRef = useRef();

  const toggleOpen = () => {
    setIsSubmenuOpen(!isSubmenuOpen);
  };

  useEffect(() => {
    if (isSubmenuOpen) {
      submenuRef.current.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [isSubmenuOpen]);

  return (
    <Container>
      <Scrollable disableOverflow={props.bellActive}>
        <ScrollableInner>
          {locale && (
            <Language
              locale={locale}
              locales={props.apiConfigs.locales}
              showLoader={!!props.changeLocaleFetching}
              onLocaleChange={props.onLocaleChange}
              hideLangSelect={props.hideLangSelect}
            />
          )}

          {items.map((item, i) => {
            let ItemComponent = item.component || Item;

            if (item.show !== undefined && item.show === false) {
              return null;
            }

            if (item.showDeposit !== undefined && item.showDeposit === false) {
              return null;
            }

            if (FeatureFlags.enableTournaments === false) {
              if (item.href === "/tournaments") {
                return null;
              }
            }

            return (
              <ItemComponent
                key={i}
                pathname={pathname}
                onClick={onClick}
                title={item.title}
                Icon={item.Icon}
                href={item.href}
                externalHref={item.externalHref}
                bellActive={props.bellActive}
                onBellClick={props.onBellClick}
              />
            );
          })}

          {!isEztrader && (
            <div>
              <SupportItem active={isSubmenuOpen} onClick={toggleOpen}>
                <IconView active={isSubmenuOpen}>
                  <SupportIcon />
                </IconView>
                <Gettext>Education / Support</Gettext>
              </SupportItem>

              {isSubmenuOpen && (
                <Submenu ref={submenuRef}>
                  <StyledLink
                    to="/sightseeing"
                    onClick={(e) => {
                      gaReducer("ACTIVATE_TOUR_FROM_SIDEBAR");
                      if (isIronTerminalIosApp) {
                        e && e.preventDefault();
                        iOSAppPostMessage("gotoTutorial");
                      }
                    }}
                  >
                    <Gettext>Start Welcome Tour</Gettext>
                  </StyledLink>
                  {simpleItems.map((item, i) => {
                    if (item.show !== undefined && item.show === false) {
                      return null;
                    }

                    return (
                      <SimpleLink
                        key={i}
                        title={item.title}
                        href={item.href}
                        onClick={onClick}
                        pathname={pathname}
                      />
                    );
                  })}
                </Submenu>
              )}
            </div>
          )}

          {isLoggedIn === true && (
            <Item
              pathname={pathname}
              href="/logout"
              replace
              title={<Gettext>Logout</Gettext>}
              Icon={QuitIcon}
            />
          )}
        </ScrollableInner>
      </Scrollable>
    </Container>
  );
}

export default withRouter(withData(["apiConfigs"])(withDeposit(Menu)));



// WEBPACK FOOTER //
// ./src/components/Navigation/Menu.js